import React, { useEffect, useState } from 'react';
import { Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap-5';

import { offMarketAssociatedPropertiesInfo } from 'bundles/connect/helpers/prospect_api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faCircleUser } from '@fortawesome/pro-solid-svg-icons';
import LlcDetails from './llc_details';
import { isEmpty } from 'lodash';
import type { AssociatedPropertiesInfo, FAAddress, FAOwner } from 'bundles/connect/types';
import { useDispatch, useSelector } from 'react-redux';
import { selectOffMarketOcCompany } from '../store/selectors/off_market_property';
import { selectCallConsoleOcCompany } from '../store/selectors/call_console';
import EmbeddedMode from 'bundles/connect/components/embedded_mode';
import { setAssociatedSearchFilters } from '../store/actions/criteria';
import { clearOffMarketProperty } from '../store/actions/off_market_property';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { USD } from 'helpers/formatters/number_formats';

type Props = {
  faOwners?: FAOwner[];
  fromConsole?: boolean;
  ownerOccupied?: boolean;
  ownerAddress?: FAAddress;
  insightsPropertyId: string;
}

const ASSOCIATED_PLACEHOLDER_DATA = {
  total: 0,
  totalValue: 0,
  propertyTypes: []
};

export default function Ownership(
  { faOwners, fromConsole = false, ownerOccupied, ownerAddress, insightsPropertyId }: Props
) {
  const llcData = useSelector(fromConsole ? selectCallConsoleOcCompany : selectOffMarketOcCompany);
  const [associatedPropertiesInfo, setAssociatedPropertiesInfo] = useState<AssociatedPropertiesInfo>(ASSOCIATED_PLACEHOLDER_DATA);
  const owner = faOwners?.[0];
  const dispatch = useDispatch();

  const hasFullAddress = [
    ownerAddress?.address,
    ownerAddress?.city,
    ownerAddress?.state,
    ownerAddress?.zip
  ].every(val => !isEmpty(val));

  const fullAddress = hasFullAddress
    ? `${ownerAddress?.address}, ${ownerAddress?.city}, ${ownerAddress?.state} ${ownerAddress?.zip}`
    : null;

  useEffect(() => {
    offMarketAssociatedPropertiesInfo(insightsPropertyId)
      .then(setAssociatedPropertiesInfo);
  }, []);

  const handleEmbeddedAssociatedPropertiesClick = () => {
    const associatedPropertiesFilters = {
      ownerAddress: ownerAddress?.address || '',
      ownerCity: ownerAddress?.city || '',
      ownerState: ownerAddress?.state || '',
      ownerZip: ownerAddress?.zip || ''
    };

    dispatch(setAssociatedSearchFilters(associatedPropertiesFilters));
    dispatch(clearOffMarketProperty());
  };

  const dataColumn = (label, value) => {
    if (!value) return null;

    return (
      <Col className="d-flex flex-column bg-light rounded p-3">
        <span className="fs-large fw-semibold">{value}</span>
        <span className="privacy-mask text-muted fs-xs">{label}</span>
      </Col>
    );
  };

  let associatedPropertiesUrl, rethinkAssociatedPropertiesLink;
  if (!associatedPropertiesInfo.error) {
    associatedPropertiesUrl = '/prospect/search?' + [
      `ownerAddress=${ownerAddress?.address || ''}`,
      `ownerCity=${ownerAddress?.city || ''}`,
      `ownerState=${ownerAddress?.state || ''}`,
      `ownerZip=${ownerAddress?.zip || ''}`
    ].join('&');

    rethinkAssociatedPropertiesLink = (
      (associatedPropertiesInfo?.total || 0) > 1 ? (
        <Button
          className="text-decoration-underline p-0"
          variant="link"
          onClick={handleEmbeddedAssociatedPropertiesClick}
        >
          {associatedPropertiesInfo.total} Properties
        </Button>
      ) : 'No Properties Available'
    );

  }

  const associatedPropertiesLink = (
    (associatedPropertiesInfo?.total || 0) > 1 ? (
      <a
        className="text-decoration-underline"
        href={associatedPropertiesUrl}
        rel="noopener noreferrer"
        target="_blank"
      >
        {associatedPropertiesInfo.total} Properties
        <FontAwesomeIcon className="ms-1" icon={faArrowUpRightFromSquare} size="sm" />
      </a>
    ) : 'No Properties Available'
  );

  if (!owner || !owner.fullName?.trim() || owner.fullName.includes('Not Available')) return null;

  return (
    <div className="mb-3 d-flex flex-column gap-2">
      <div className="d-flex align-items-center gap-2">
        <FontAwesomeIcon className="text-primary" icon={faCircleUser} />
        <h6 className="m-0 fw-bolder">Ownership</h6>
      </div>

      <Row className="align-items-start">
        <Col className="d-flex flex-column gap-1" xs={12}>
          <Row className="gx-0 gap-1 align-items-start justify-content-between">
            {dataColumn('Owner Name', owner.fullName)}
            {dataColumn('Owner Occupied', ownerOccupied ? 'Yes' : 'No')}
            {dataColumn('Owner Type', owner.isCorporation ? 'Company' : 'Private')}
          </Row>
          <Row className="gx-0 gap-1 align-items-start justify-content-between">
            {dataColumn('Owner Address', fullAddress)}
          </Row>
          <Row className="gx-0 gap-1 align-items-start justify-content-between">
            {associatedPropertiesInfo.total == 0 && (
              dataColumn('Associated Properties Nationally', 'No Properties Available')
            )}
            {(associatedPropertiesInfo?.total || 0) > 0 && (
              <>
                <EmbeddedMode>{dataColumn('Associated Properties Nationally', associatedPropertiesLink)}</EmbeddedMode>
                <EmbeddedMode display forBuildoutCrm forRethink>
                  {dataColumn('Associated Properties Nationally', rethinkAssociatedPropertiesLink)}
                </EmbeddedMode>
                <Col className="d-flex flex-column bg-light rounded p-3">
                  <span className="fs-large fw-semibold">
                    {USD.format(associatedPropertiesInfo.totalValue, { round: true })}
                    <OverlayTrigger
                      overlay={<Tooltip id="tax-assessed-tooltip">
                        This number is calculated based on the combined tax assessed value of all the properties associated to this owner.
                      </Tooltip>}
                      placement="top"
                    >
                      <FontAwesomeIcon className="ms-2" icon={faCircleInfo} size="sm" />
                    </OverlayTrigger>
                  </span>
                  <span className="privacy-mask text-muted fs-xs">Total Tax Assessed Value</span>
                </Col>
                {dataColumn('Property Types', associatedPropertiesInfo.propertyTypes.join(', '))}
              </>
            )}
          </Row>
        </Col>
      </Row>
      {llcData && <LlcDetails llcData={llcData} />}
    </div>
  );
}
